import React, { useState } from 'react';
import { useAdminScope, ScopeCity } from '../../lib/api';
import LoadingState from '../../components/common/LoadingState';
import ErrorState from '../../components/common/ErrorState';
import ReportPeriodSelector from '../../components/reports/ReportPeriodSelector';
import AalborgMonthlyReport from '../../components/reports/AalborgMonthlyReport';
import KkSettlementReport from '../../components/reports/KkSettlementReport';

/**
 * Reports is rendered from the signed-in account's scope: one card per city it
 * may see, and nothing else. A Copenhagen viewer opens this page and finds a
 * single København card — no Aalborg button that would only ever return an empty
 * table for them, and no hint that other municipalities exist.
 *
 * The scope is a drawing instruction, not a permission. Adding a card to this
 * list in a browser gets you a card; the endpoints behind it still scope their
 * own data and still refuse a municipality the account isn't granted.
 */
const ReportsPage: React.FC = () => {
    const { scope, isLoading, error } = useAdminScope();
    const [year, setYear] = useState(new Date().getFullYear());
    const [month, setMonth] = useState(new Date().getMonth() + 1);

    const renderCity = (city: ScopeCity) => {
        switch (city.reporting) {
            case 'aalborg-monthly':
                return <AalborgMonthlyReport key={city.id} city={city} year={year} month={month} />;
            case 'kk-settlement':
                return <KkSettlementReport key={city.id} city={city} year={year} month={month} />;
            default:
                // The city scopes sessions but has no settlement generator yet.
                return null;
        }
    };

    if (isLoading) {
        return <LoadingState message="Loading your reports..." />;
    }

    if (error || !scope) {
        return <ErrorState error={error?.message || 'Could not load your access scope'} />;
    }

    const reportableCities = scope.cities.filter(city => city.reporting !== null);

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Monthly Reports</h1>
                    <p className="text-gray-600">
                        {reportableCities.length === 1
                            ? `Settlement reporting for ${reportableCities[0].settlementName}`
                            : 'Generate and verify municipality settlement reports'}
                    </p>
                </div>
            </div>

            {scope.unknownCities.length > 0 && (
                <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
                    Your account is configured for a municipality this panel does not recognise
                    ({scope.unknownCities.join(', ')}). Contact FreeSpot — some data may be missing.
                </div>
            )}

            {reportableCities.length === 0 ? (
                <div className="bg-white border rounded-lg p-8 text-center">
                    <p className="text-gray-900 font-medium">No reports available for your account</p>
                    <p className="text-sm text-gray-600 mt-1">
                        No municipality with monthly settlement reporting is assigned to you. Contact
                        FreeSpot if you expected to see one here.
                    </p>
                </div>
            ) : (
                <>
                    <ReportPeriodSelector
                        year={year}
                        month={month}
                        onYearChange={setYear}
                        onMonthChange={setMonth}
                    />
                    {reportableCities.map(renderCity)}
                </>
            )}
        </div>
    );
};

export default ReportsPage;
